import { useEffect, useRef, useState } from 'react';

// Recomputed from Date.now() each tick (not decremented) so a backgrounded tab can't drift the countdown.
const computeSecondsLeft = (expiresAt: string | null): number => {
  if (!expiresAt) return 0;
  const deltaMs = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(deltaMs / 1000));
};

const formatMmSs = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const formatHhMmSs = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, '0'))
    .join(':');
};

export type CountdownFormat = 'mm:ss' | 'hh:mm:ss';

export const useCountdown = (
  expiresAt: string | null,
  options?: { format?: CountdownFormat },
) => {
  const format = options?.format ?? 'mm:ss';
  const [secondsLeft, setSecondsLeft] = useState(() =>
    computeSecondsLeft(expiresAt),
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );

  useEffect(() => {
    const initial = computeSecondsLeft(expiresAt);
    setSecondsLeft(initial);
    clearInterval(intervalRef.current);
    // Nothing to tick toward once the offer is (or starts) expired.
    if (!expiresAt || initial <= 0) return;

    intervalRef.current = setInterval(() => {
      const next = computeSecondsLeft(expiresAt);
      setSecondsLeft(next);
      if (next <= 0) clearInterval(intervalRef.current);
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [expiresAt]);

  const mmss = formatMmSs(secondsLeft);

  return {
    mmss,
    formatted: format === 'hh:mm:ss' ? formatHhMmSs(secondsLeft) : mmss,
    secondsLeft,
    hasExpired: secondsLeft <= 0,
  };
};
